// src/betslip/hooks/use-betslip.hook.ts
import { useCallback } from "react";

// src/betslip/utils/betslip-helpers.utils.ts
var getUpdateMode = (kambiApi, mode) => {
  if (mode === "REPLACE") {
    return kambiApi.BETSLIP_OUTCOMES_ARGS.UPDATE_REPLACE;
  }
  return kambiApi.BETSLIP_OUTCOMES_ARGS.UPDATE_APPEND;
};
var getCouponType = (kambiApi, type) => {
  if (type === "COMBINATION") {
    return kambiApi.BETSLIP_OUTCOMES_ARGS.TYPE_COMBINATION;
  }
  if (type === "SYSTEM") {
    return kambiApi.BETSLIP_OUTCOMES_ARGS.TYPE_SYSTEM;
  }
  return kambiApi.BETSLIP_OUTCOMES_ARGS.TYPE_SINGLE;
};

// src/betslip/utils/betslip-operations.utils.ts
var addOutcomesToBetslip = (kambiApi, outcomes, options) => {
  if (!kambiApi) return;
  const payload = {
    outcomes: outcomes.map((outcome) => outcome.id),
    updateMode: getUpdateMode(kambiApi, options?.updateMode),
    couponType: getCouponType(kambiApi, options?.couponType),
    stakes: options?.stakes
  };
  kambiApi.set(kambiApi.BETSLIP_OUTCOMES, payload);
};
var removeOutcomesFromBetslip = (kambiApi, outcomeIds, currentBetslipState) => {
  if (!kambiApi) return;
  if (!currentBetslipState) {
    const payload2 = {
      outcomes: [],
      updateMode: kambiApi.BETSLIP_OUTCOMES_ARGS.UPDATE_REPLACE,
      couponType: kambiApi.BETSLIP_OUTCOMES_ARGS.TYPE_SINGLE
    };
    kambiApi.set(kambiApi.BETSLIP_OUTCOMES, payload2);
    return;
  }
  const remainingOutcomes = currentBetslipState.outcomes.filter((outcome) => !outcomeIds.includes(outcome.id)).map((outcome) => outcome.id);
  const payload = {
    outcomes: remainingOutcomes,
    updateMode: kambiApi.BETSLIP_OUTCOMES_ARGS.UPDATE_REPLACE,
    couponType: currentBetslipState.type
  };
  kambiApi.set(kambiApi.BETSLIP_OUTCOMES, payload);
};
var addPrepacksToBetslip = (kambiApi, prepacks) => {
  if (!kambiApi) return;
  const allOutcomes = prepacks.flatMap((prepack) => prepack.outcomes);
  addOutcomesToBetslip(kambiApi, allOutcomes, { updateMode: "APPEND" });
};
var removePrepacksFromBetslip = (kambiApi, outcomeIdsToRemove, currentBetslipState) => {
  if (!kambiApi) return;
  if (!currentBetslipState) {
    removeOutcomesFromBetslip(kambiApi, []);
    return;
  }
  const remainingOutcomes = currentBetslipState.outcomes.filter((outcome) => !outcomeIdsToRemove.includes(outcome.id)).map((outcome) => outcome.id);
  const payload = {
    outcomes: remainingOutcomes,
    updateMode: kambiApi.BETSLIP_OUTCOMES_ARGS.UPDATE_REPLACE,
    couponType: kambiApi.BETSLIP_OUTCOMES_ARGS.TYPE_SINGLE
  };
  kambiApi.set(kambiApi.BETSLIP_OUTCOMES, payload);
};
var addPrepackSelectionToBetslip = (kambiApi, selection) => {
  if (!kambiApi) return;
  const outcomeIds = selection.selectedOutcomes.map(
    (outcomeId) => Number.parseInt(outcomeId, 10)
  );
  const payload = {
    outcomes: outcomeIds,
    updateMode: kambiApi.BETSLIP_OUTCOMES_ARGS.UPDATE_APPEND,
    couponType: kambiApi.BETSLIP_OUTCOMES_ARGS.TYPE_SINGLE
  };
  kambiApi.set(kambiApi.BETSLIP_OUTCOMES, payload);
};
var removePrepackSelectionFromBetslip = (kambiApi, outcomeIdsToRemove, currentBetslipState) => {
  if (!kambiApi) return;
  if (!currentBetslipState) {
    removeOutcomesFromBetslip(kambiApi, []);
    return;
  }
  const remainingOutcomes = currentBetslipState.outcomes.filter((outcome) => !outcomeIdsToRemove.includes(outcome.id)).map((outcome) => outcome.id);
  const payload = {
    outcomes: remainingOutcomes,
    updateMode: kambiApi.BETSLIP_OUTCOMES_ARGS.UPDATE_REPLACE,
    couponType: currentBetslipState.type
  };
  kambiApi.set(kambiApi.BETSLIP_OUTCOMES, payload);
};
var clearBetslip = (kambiApi) => {
  if (!kambiApi) return;
  const payload = {
    outcomes: [],
    updateMode: kambiApi.BETSLIP_OUTCOMES_ARGS.UPDATE_REPLACE,
    couponType: kambiApi.BETSLIP_OUTCOMES_ARGS.TYPE_SINGLE
  };
  kambiApi.set(kambiApi.BETSLIP_OUTCOMES, payload);
};

// src/betslip/hooks/use-betslip.hook.ts
var useBetslip = (kambiApi, currentBetslipState) => {
  const addOutcomes = useCallback(
    (outcomes) => {
      if (!kambiApi) return;
      addOutcomesToBetslip(kambiApi, outcomes);
    },
    [kambiApi]
  );
  const removeOutcomes = useCallback(
    (outcomeIds) => {
      if (!kambiApi) return;
      const numericIds = outcomeIds.map((id) => Number.parseInt(id, 10));
      removeOutcomesFromBetslip(kambiApi, numericIds, currentBetslipState);
    },
    [kambiApi, currentBetslipState]
  );
  const addPrepacks = useCallback(
    (prepacks) => {
      if (!kambiApi) return;
      addPrepacksToBetslip(kambiApi, prepacks);
    },
    [kambiApi]
  );
  const removePrepacks = useCallback(
    (outcomeIdsToRemove) => {
      if (!kambiApi) return;
      removePrepacksFromBetslip(
        kambiApi,
        outcomeIdsToRemove,
        currentBetslipState
      );
    },
    [kambiApi, currentBetslipState]
  );
  const addPrepackSelection = useCallback(
    (selection) => {
      if (!kambiApi) return;
      addPrepackSelectionToBetslip(kambiApi, selection);
    },
    [kambiApi]
  );
  const removePrepackSelection = useCallback(
    (outcomeIdsToRemove) => {
      if (!kambiApi) return;
      removePrepackSelectionFromBetslip(
        kambiApi,
        outcomeIdsToRemove,
        currentBetslipState
      );
    },
    [kambiApi, currentBetslipState]
  );
  const clearBetslipHandler = useCallback(() => {
    if (!kambiApi) return;
    clearBetslip(kambiApi);
  }, [kambiApi]);
  return {
    addOutcomes,
    removeOutcomes,
    addPrepacks,
    removePrepacks,
    addPrepackSelection,
    removePrepackSelection,
    clearBetslip: clearBetslipHandler
  };
};

// src/betslip/hooks/use-betslip-with-state.hook.ts
import { useCallback as useCallback2, useEffect, useState } from "react";
var useBetslipWithState = (kambiApi) => {
  const [betslipState, setBetslipState] = useState(null);
  const betslipOperations = useBetslip(kambiApi, betslipState || void 0);
  const handleBetslipUpdate = useCallback2(
    (response) => {
      if (response.type === "BetslipState" || response.type === "BETSLIP_OUTCOMES" || response.data && response.data.outcomes !== void 0) {
        setBetslipState(response.data || null);
      }
    },
    []
  );
  useEffect(() => {
    if (!kambiApi) {
      return;
    }
    const setupBetslipSubscription = async () => {
      try {
        const api = await window?.KambiWidget?.ready;
        if (api) {
          api.subscribe(handleBetslipUpdate);
          api.request(api.BETSLIP_OUTCOMES);
        }
      } catch (error) {
        console.error("Error setting up betslip subscription:", error);
      }
    };
    setupBetslipSubscription();
  }, [kambiApi, handleBetslipUpdate]);
  const isOutcomeInBetslip = useCallback2(
    (outcomeId) => {
      return betslipState?.outcomes.some((outcome) => outcome.id === outcomeId) || false;
    },
    [betslipState]
  );
  const toggleOutcome = useCallback2(
    (outcome) => {
      if (!kambiApi) return;
      if (isOutcomeInBetslip(outcome.id)) {
        betslipOperations.removeOutcomes([outcome.id.toString()]);
      } else {
        betslipOperations.addOutcomes([outcome]);
      }
    },
    [kambiApi, isOutcomeInBetslip, betslipOperations]
  );
  return {
    betslipState,
    isOutcomeInBetslip,
    ...betslipOperations,
    toggleOutcome
  };
};

// src/icons/boosted/boosted.tsx
import { forwardRef } from "react";
import { jsx } from "react/jsx-runtime";
var Boosted = forwardRef(
  (props, ref) => /* @__PURE__ */ jsx(
    "svg",
    {
      ref,
      width: "14",
      height: "14",
      viewBox: "0 0 14 14",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      role: "img",
      "aria-hidden": "true",
      ...props,
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M8.23623 0C9.97112 0 11.505 0.614049 12.5761 1.74316C14.9117 4.20553 14.3115 8.38336 11.2382 11.0557C8.16462 13.7281 3.76266 13.8989 1.42666 11.4365C0.301243 10.2503 -0.182864 8.63165 0.0624051 6.88086C0.309923 5.11352 1.26954 3.4244 2.76455 2.12402C4.25982 0.823987 6.10898 0.0712941 7.97061 0.00488281C8.05968 0.00194249 8.14804 8.9348e-08 8.23623 0ZM9.70205 2.50781C9.63818 2.45034 9.51834 2.4606 9.42959 2.53711L7.31631 4.3584C7.17696 4.47887 7.21514 4.66282 7.37979 4.66309H7.99307L6.78897 9.03613C6.56612 9.95754 5.93731 10.4685 5.37491 10.4688H6.72061C7.62377 10.4686 8.55021 9.78484 8.78115 8.94922L9.96182 4.66309H10.6034C10.7679 4.66302 10.9081 4.47893 10.8349 4.3584L9.72549 2.53711L9.70205 2.50781ZM5.31338 5.65039C5.20647 5.65039 5.11319 5.71945 5.08584 5.81836L4.12979 9.28516C3.98148 9.82367 3.4042 10.2607 2.8417 10.2607H4.60049C5.27687 10.2606 5.97094 9.74872 6.14346 9.12305L7.05655 5.80762C7.08017 5.72116 7.02392 5.65046 6.93057 5.65039H5.31338Z",
          fill: "white"
        }
      )
    }
  )
);
Boosted.displayName = "Boosted";

// src/icons/fomo/fomo-l.tsx
import { forwardRef as forwardRef2 } from "react";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var FomoL = forwardRef2(
  (props, ref) => /* @__PURE__ */ jsxs(
    "svg",
    {
      ref,
      width: "16",
      height: "17",
      viewBox: "0 0 16 17",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      role: "img",
      "aria-hidden": "true",
      ...props,
      children: [
        /* @__PURE__ */ jsx2(
          "path",
          {
            d: "M2 10.6936C2 13.5211 4.11759 15.8855 6.95868 16.4705C7.41186 16.5762 7.88241 16.625 8.36186 16.625C8.91949 16.625 9.46864 16.5438 10.0002 16.3894C12.2222 15.7231 14 13.7893 14 11.5225C14 9.78364 13.3028 8.40252 12.1439 7.0781C10.6797 5.38805 8.38771 3.85247 9.00657 0.375C8.84089 0.537564 6.27881 2.51185 5.23305 5.21758C4.68411 6.63129 4.65784 8.19136 5.33771 9.8976C5.33771 9.8976 3.84746 9.77573 2.81059 7.79315C2.2964 8.65437 2.00021 9.63746 2.00021 10.6938L2 10.6936Z",
            fill: "#FF5353"
          }
        ),
        /* @__PURE__ */ jsx2(
          "path",
          {
            d: "M6 13.8449C6 14.6173 6.54505 15.3085 7.26449 15.6743C7.6424 15.7625 8.03481 15.8031 8.43463 15.8031C8.89965 15.8031 9.3576 15.7354 9.80088 15.6066C10.5348 15.1662 11 14.387 11 13.4993C11 13.0589 10.8765 12.6456 10.6657 12.2865C10.2297 13.1131 9.61201 13.1673 9.61201 13.1673C9.89541 12.4558 9.88092 11.8053 9.65565 11.2159C9.21961 10.0912 8.15124 9.26464 8.07862 9.19678C8.34028 10.6468 7.38092 11.2837 6.77049 11.9883C6.29081 12.5439 6.00017 13.1198 6.00017 13.8448L6 13.8449Z",
            fill: "#FFBEBE"
          }
        )
      ]
    }
  )
);
FomoL.displayName = "FomoL";

// src/icons/fomo/fomo-m.tsx
import { forwardRef as forwardRef3 } from "react";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var FomoM = forwardRef3(
  (props, ref) => /* @__PURE__ */ jsxs2(
    "svg",
    {
      ref,
      width: "16",
      height: "16",
      viewBox: "0 0 16 16",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      role: "img",
      "aria-hidden": "true",
      ...props,
      children: [
        /* @__PURE__ */ jsx3(
          "path",
          {
            d: "M2 9.5249C2 12.1348 4.11759 14.3173 6.95868 14.8574C7.41186 14.955 7.88241 15 8.36186 15C8.91949 15 9.46864 14.9251 10.0002 14.7825C12.2222 14.1675 14 12.3824 14 10.29C14 8.68489 13.3028 7.41002 12.1439 6.18748C10.6797 4.62743 8.38771 3.20998 9.00657 0C8.84089 0.150059 6.27881 1.97248 5.23305 4.47008C4.68411 5.77504 4.65784 7.2151 5.33771 8.7901C5.33771 8.7901 3.84746 8.6776 2.81059 6.84752C2.2964 7.64249 2.00021 8.54997 2.00021 9.52508L2 9.5249Z",
            fill: "#FA9600"
          }
        ),
        /* @__PURE__ */ jsx3(
          "path",
          {
            d: "M6 12.9251C6 13.7435 6.54505 14.4759 7.26449 14.8635C7.6424 14.9569 8.03481 15 8.43463 15C8.89965 15 9.3576 14.9283 9.80088 14.7918C10.5348 14.3251 11 13.4995 11 12.5589C11 12.0922 10.8765 11.6543 10.6657 11.2738C10.2297 12.1496 9.61201 12.2071 9.61201 12.2071C9.89541 11.4532 9.88092 10.764 9.65565 10.1395C9.21961 8.94774 8.15124 8.07191 8.07862 8C8.34028 9.53645 7.38092 10.2112 6.77049 10.9579C6.29081 11.5466 6.00017 12.1568 6.00017 12.9249L6 12.9251Z",
            fill: "#FFE8C6"
          }
        )
      ]
    }
  )
);
FomoM.displayName = "FomoM";

// src/icons/fomo/fomo-s.tsx
import { forwardRef as forwardRef4 } from "react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var FomoS = forwardRef4(
  (props, ref) => /* @__PURE__ */ jsxs3(
    "svg",
    {
      ref,
      width: "16",
      height: "16",
      viewBox: "0 0 16 16",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      role: "img",
      "aria-hidden": "true",
      ...props,
      children: [
        /* @__PURE__ */ jsx4(
          "path",
          {
            d: "M2 9.5249C2 12.1348 4.11759 14.3173 6.95868 14.8574C7.41186 14.955 7.88241 15 8.36186 15C8.91949 15 9.46864 14.9251 10.0002 14.7825C12.2222 14.1675 14 12.3824 14 10.29C14 8.68489 13.3028 7.41002 12.1439 6.18748C10.6797 4.62743 8.38771 3.20998 9.00657 0C8.84089 0.150059 6.27881 1.97248 5.23305 4.47008C4.68411 5.77504 4.65784 7.2151 5.33771 8.7901C5.33771 8.7901 3.84746 8.6776 2.81059 6.84752C2.2964 7.64249 2.00021 8.54997 2.00021 9.52508L2 9.5249Z",
            fill: "#FAD749"
          }
        ),
        /* @__PURE__ */ jsx4(
          "path",
          {
            d: "M6 12.9251C6 13.7435 6.54505 14.4759 7.26449 14.8635C7.6424 14.9569 8.03481 15 8.43463 15C8.89965 15 9.3576 14.9283 9.80088 14.7918C10.5348 14.3251 11 13.4995 11 12.5589C11 12.0922 10.8765 11.6543 10.6657 11.2738C10.2297 12.1496 9.61201 12.2071 9.61201 12.2071C9.89541 11.4532 9.88092 10.764 9.65565 10.1395C9.21961 8.94774 8.15124 8.07191 8.07862 8C8.34028 9.53645 7.38092 10.2112 6.77049 10.9579C6.29081 11.5466 6.00017 12.1568 6.00017 12.9249L6 12.9251Z",
            fill: "#FFF5CD"
          }
        )
      ]
    }
  )
);
FomoS.displayName = "FomoS";

// src/icons/heading/heading.tsx
import { forwardRef as forwardRef5 } from "react";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var Heading = forwardRef5(
  (props, ref) => /* @__PURE__ */ jsxs4(
    "svg",
    {
      ref,
      width: "14",
      height: "21",
      viewBox: "0 0 14 21",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      role: "img",
      "aria-hidden": "true",
      ...props,
      children: [
        /* @__PURE__ */ jsx5("path", { d: "M2 21V11L7 12V17.5L2 21Z", fill: "#00DD70", fillOpacity: "0.3" }),
        /* @__PURE__ */ jsx5("path", { d: "M12 21V11L7 12V17.5L12 21Z", fill: "#00DD70", fillOpacity: "0.5" }),
        /* @__PURE__ */ jsx5("circle", { cx: "7", cy: "7", r: "7", fill: "#FAD749" }),
        /* @__PURE__ */ jsx5("circle", { cx: "7", cy: "7", r: "5", fill: "black" })
      ]
    }
  )
);
Heading.displayName = "Heading";

// src/icons/sport/football.tsx
import { forwardRef as forwardRef6 } from "react";
import { jsx as jsx6 } from "react/jsx-runtime";
var Football = forwardRef6((props, ref) => /* @__PURE__ */ jsx6(
  "svg",
  {
    ref,
    width: "12",
    height: "12",
    viewBox: "0 0 12 12",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    role: "img",
    "aria-hidden": "true",
    "data-testid": "football-icon",
    ...props,
    children: /* @__PURE__ */ jsx6(
      "path",
      {
        d: "M7.0562 0.0948309C10.3174 0.678468 12.4887 3.79534 11.9052 7.0565C11.3219 10.3177 8.20418 12.4886 4.94298 11.9053C1.68167 11.3217 -0.488642 8.20419 0.0947846 4.94353C0.678375 1.68241 3.795 -0.48886 7.0562 0.0948309ZM5.12754 9.2626C4.75125 9.62249 4.00088 9.9941 3.66524 10.0654C3.97331 10.6466 4.47804 11.0597 4.79522 11.2774C4.88397 11.2986 4.97395 11.3168 5.06448 11.3331C5.77828 11.4607 6.48457 11.4424 7.15147 11.3002C7.20674 11.2703 7.26122 11.2405 7.31438 11.2099C7.62285 11.0314 7.8982 10.8369 8.13495 10.6301C7.89969 10.4778 7.69708 10.1593 7.51707 9.65963C7.15218 9.70044 6.74881 9.68184 6.31701 9.60434C5.77428 9.50743 5.32547 9.3423 5.12754 9.2626ZM1.84777 8.51165C1.69548 8.62899 1.5523 8.74824 1.41795 8.86906C2.09252 9.94655 3.14267 10.7844 4.42138 11.177C4.1127 10.9236 3.73646 10.5452 3.49901 10.0602C3.03004 9.88908 2.11839 8.93888 1.84777 8.51165ZM9.27636 10.3347C8.89771 10.5374 8.56868 10.6255 8.29958 10.6783C8.11334 10.8488 7.90223 11.0108 7.66901 11.1626C8.24941 10.9778 8.79269 10.6963 9.27636 10.3347ZM5.98897 5.25377C5.95821 5.27859 5.90428 5.32254 5.83441 5.38278C5.66714 5.52565 5.50695 5.67524 5.35618 5.82556C5.04104 6.14201 4.62376 6.61647 4.41976 7.09015C4.6043 7.72974 4.82857 8.46258 5.15961 9.11816C5.16534 9.12085 5.17235 9.12328 5.17905 9.12637C5.23555 9.14884 5.32613 9.18465 5.44407 9.2249C5.64972 9.29616 5.97244 9.39472 6.34241 9.46105C6.77952 9.53913 7.18424 9.55579 7.54631 9.50949C7.98708 9.10641 8.46782 8.5444 9.01406 7.79202C8.98623 7.01615 8.45409 6.05807 8.22469 5.68C8.20162 5.64199 8.18083 5.60757 8.16165 5.57754C8.06678 5.55926 7.96841 5.54098 7.87113 5.52314C7.4319 5.44474 7.04001 5.39395 6.66144 5.34485C6.43069 5.31506 6.21212 5.28796 5.98897 5.25377ZM11.4477 6.26218C11.343 6.7097 11.1038 7.43956 10.5959 7.9775C10.5912 8.46122 10.4569 8.95952 10.1965 9.46275C10.7811 8.76216 11.1975 7.9046 11.3698 6.94176C11.4108 6.71441 11.4367 6.48741 11.4477 6.26218ZM0.878859 4.23145C0.792279 4.48763 0.721786 4.75331 0.672846 5.02793C0.435026 6.35851 0.703763 7.66241 1.34061 8.7422C1.48144 8.61759 1.63108 8.4935 1.78884 8.373C1.73388 7.75109 2.08098 7.08487 2.31046 6.72087C1.83333 5.96773 1.66603 4.7949 1.6292 4.47322C1.45332 4.43255 1.12186 4.34767 0.878859 4.23145ZM9.15706 3.68566C9.01477 4.36004 8.71035 5.00711 8.29615 5.51741C8.5148 5.8676 9.10919 6.89342 9.15818 7.74714C9.19331 7.75634 9.2359 7.7649 9.28539 7.77356C9.62909 7.83516 10.2259 7.88028 10.4787 7.88941C11.0919 7.24877 11.3039 6.28106 11.3544 5.99776C11.3595 5.97045 11.3626 5.94845 11.3658 5.93069C11.3001 5.29557 10.973 4.46673 10.6946 3.99677C10.4797 3.88558 10.2238 3.80109 9.93332 3.74925C9.69466 3.70677 9.43432 3.68571 9.15706 3.68566ZM3.27487 2.65873C2.76949 3.1741 2.23753 3.74545 1.77147 4.43576C1.77618 4.47785 1.78473 4.54756 1.79812 4.63667C1.81881 4.78365 1.85687 5.01307 1.91664 5.27381C2.0102 5.68356 2.17876 6.2553 2.45356 6.6742C2.56201 6.6944 2.68461 6.71702 2.82792 6.74271C3.00964 6.77517 3.20124 6.80834 3.39681 6.84278C3.69832 6.89541 4.00766 6.94937 4.29797 7.00279C4.66075 6.19678 5.55498 5.42016 5.87135 5.16159C5.71576 4.64426 5.59228 4.09541 5.47104 3.56515C5.43484 3.40384 5.39725 3.23803 5.35935 3.07481C5.05621 2.91062 4.61769 2.76668 4.18029 2.68824L4.17933 2.68805L4.18437 2.65847L4.18252 2.65834L4.17713 2.68777C3.8051 2.62138 3.47798 2.6105 3.27487 2.65873ZM7.60574 0.787564C7.47754 0.832789 7.34493 0.871266 7.21636 0.889132C7.29162 1.1704 7.39721 1.68417 7.41824 2.11448C7.82697 2.28053 8.86332 3.18844 9.1263 3.54067C9.73958 3.53701 10.2734 3.63599 10.6812 3.82719C10.6908 3.7234 10.6999 3.61825 10.7088 3.52354C10.7176 3.42974 10.7249 3.34593 10.7311 3.27661C10.0599 2.11051 8.96114 1.19896 7.60574 0.787564ZM3.2542 1.30898C3.20393 1.71881 3.23035 2.20564 3.28708 2.5083C3.51159 2.46588 3.83413 2.47893 4.20522 2.54511C4.65439 2.62533 5.10541 2.77356 5.42037 2.94188C5.70802 2.75279 6.75391 2.21119 7.27221 2.11378C7.24948 1.67515 7.13455 1.1257 7.05605 0.860411C6.88469 0.778644 6.71648 0.680739 6.53856 0.577314L6.53647 0.575535C5.35746 0.462223 4.21684 0.739618 3.2542 1.30898Z",
        fill: "white"
      }
    )
  }
));
Football.displayName = "Football";

// src/icons/sport/pin.tsx
import { forwardRef as forwardRef7 } from "react";
import { jsx as jsx7 } from "react/jsx-runtime";
var Pin = forwardRef7(
  (props, ref) => /* @__PURE__ */ jsx7(
    "svg",
    {
      ref,
      width: "8",
      height: "12",
      viewBox: "0 0 8 12",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      role: "img",
      "aria-hidden": "true",
      "data-testid": "pin-icon",
      ...props,
      children: /* @__PURE__ */ jsx7(
        "path",
        {
          d: "M3.61145 9.00049C3.51818 9.00064 3.44641 9.03995 3.44641 9.09131V10.4448C3.45362 11.3028 3.5758 11.9992 3.72668 12.0005H4.17981C4.33087 12.0005 4.44639 11.3036 4.44641 10.4448V9.09131C4.44641 9.03986 4.36708 9.00049 4.27356 9.00049H3.61145ZM2.05383 -0.000488281C1.80601 -0.000232603 1.60461 0.209147 1.60461 0.467285C1.60461 0.515669 1.60483 0.564409 1.62805 0.612793C1.72885 0.935362 1.96129 1.09662 2.32532 1.08057C2.44933 1.08057 2.50391 1.13722 2.4884 1.2583L2.06165 5.72998C2.06164 5.81069 2.01519 5.86762 1.94543 5.8999C0.697782 6.44873 -0.00762796 7.06228 0.00012207 8.57959C0.000219822 8.86187 0.140127 8.99948 0.411255 8.99951H7.63489C7.82091 8.99951 7.97583 8.84557 7.99133 8.65185C8.06882 7.52185 7.61932 6.70626 6.6427 6.19775C6.45685 6.10098 6.26319 5.99618 6.08508 5.8833C6.00762 5.84293 5.961 5.7621 5.95325 5.67334L5.51868 1.20166C5.5187 1.13715 5.55823 1.08868 5.61243 1.08057H5.62708C5.89059 1.09671 6.10805 1.02373 6.29407 0.846191C6.48755 0.660628 6.50313 0.354562 6.3175 0.152832C6.23232 0.0560892 6.10821 -0.000386186 5.96887 -0.000488281H2.05383Z",
          fill: "white"
        }
      )
    }
  )
);
Pin.displayName = "Pin";

// src/icons/utils.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var renderIcon = (icon) => {
  if (typeof icon === "string") {
    return /* @__PURE__ */ jsx8("span", { className: "text-xs", children: icon });
  }
  return icon;
};
var getSportIcon = (sportType) => {
  const iconMap = {
    football: /* @__PURE__ */ jsx8(Football, { className: "w-3 h-3" }),
    basketball: "\u{1F3C0}",
    baseball: "\u26BE",
    tennis: "\u{1F3BE}",
    american_football: "\u{1F3C8}"
  };
  return iconMap[sportType] || "";
};

// src/components/buttons/toggle-button/toggle-button.tsx
import { Button } from "@repo/ui";
import { cx } from "class-variance-authority";

// src/components/buttons/toggle-button/toggle-button.const.ts
var DISABLED_BUTTON_TEXT = "Odds not available";

// src/components/buttons/toggle-button/toggle-button.hook.ts
import { createContext, useCallback as useCallback3, useContext, useState as useState2 } from "react";
var ToggleButtonContext = createContext(null);
var useToggleButtonContext = () => {
  const context = useContext(ToggleButtonContext);
  if (!context) {
    throw new Error(
      "ToggleButton components must be used within a ToggleButton"
    );
  }
  return context;
};
var useToggleButton = (props) => {
  const { disabled = false, onClick, isSelected: externalIsSelected } = props;
  const [internalIsSelected, setInternalIsSelected] = useState2(false);
  const isSelected = externalIsSelected !== void 0 ? externalIsSelected : internalIsSelected;
  const handleToggle = useCallback3(
    (e) => {
      if (disabled) return;
      if (externalIsSelected === void 0) {
        setInternalIsSelected((prev) => !prev);
      }
      if (onClick) {
        onClick(e);
      }
    },
    [onClick, disabled, externalIsSelected]
  );
  return {
    isSelected,
    handleToggle,
    disabled
  };
};

// src/components/buttons/toggle-button/toggle-button.styles.ts
import { cva } from "class-variance-authority";
var toggleButtonLayout = cva("", {
  variants: {
    layout: {
      vertical: "flex-col",
      horizontal: "gap-1"
    }
  },
  defaultVariants: { layout: "vertical" }
});
var toggleButtonClasses = {
  disabledText: "text-xs font-inter font-normal leading-[16px] bg-dark-grey-1"
};

// src/components/buttons/toggle-button/toggle-button.sub-components.tsx
import { Fragment, jsx as jsx9 } from "react/jsx-runtime";
var ToggleButtonContent = ({
  children
}) => {
  const { isSelected, disabled, disabledText } = useToggleButtonContext();
  if (disabled) {
    return /* @__PURE__ */ jsx9("div", { className: toggleButtonClasses.disabledText, children: disabledText });
  }
  return /* @__PURE__ */ jsx9(Fragment, { children: children({ isSelected, disabled }) });
};
var ToggleButtonIcon = ({
  selectedIcon,
  unselectedIcon
}) => {
  const { isSelected, disabled } = useToggleButtonContext();
  if (disabled) {
    return null;
  }
  return /* @__PURE__ */ jsx9(Fragment, { children: isSelected ? selectedIcon : unselectedIcon });
};
var ToggleButtonText = ({
  selectedText,
  unselectedText
}) => {
  const { isSelected, disabled, disabledText } = useToggleButtonContext();
  if (disabled) {
    return /* @__PURE__ */ jsx9("span", { className: toggleButtonClasses.disabledText, children: disabledText });
  }
  return /* @__PURE__ */ jsx9("span", { children: isSelected ? selectedText : unselectedText });
};

// src/components/buttons/toggle-button/toggle-button.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
var ToggleButton = ({
  children,
  className,
  layout = "vertical",
  disabledText,
  ...baseProps
}) => {
  const { isSelected, handleToggle, disabled } = useToggleButton(baseProps);
  const contextValue = {
    isSelected,
    disabled,
    handleToggle,
    disabledText: disabledText || DISABLED_BUTTON_TEXT
  };
  return /* @__PURE__ */ jsx10(ToggleButtonContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx10(
    Button,
    {
      type: "button",
      variant: "toggle",
      onClick: handleToggle,
      disabled,
      "aria-pressed": isSelected,
      pressed: isSelected,
      className: cx(toggleButtonLayout({ layout }), className),
      children
    }
  ) });
};
ToggleButton.Content = ToggleButtonContent;
ToggleButton.Icon = ToggleButtonIcon;
ToggleButton.Text = ToggleButtonText;

// src/components/buttons/utils/format-odds.utils.ts
var formatOdds = (odds) => {
  const parsed = Number(odds) / 1e3;
  if (Number.isNaN(parsed)) {
    return "";
  }
  return parsed.toFixed(2);
};

// src/components/buttons/odds-boost-button/odds-boost-button.styles.ts
import { cva as cva2 } from "class-variance-authority";
var oddsBoostButtonVariants = {
  oddsDisplay: "flex items-center gap-1",
  oldOdds: "text-dark-grey-1 line-through",
  arrow: cva2([""], {
    variants: {
      isClicked: {
        true: "text-black",
        false: "text-white"
      },
      disabled: {
        true: "text-dark-grey-1",
        false: ""
      }
    },
    defaultVariants: {
      isClicked: false,
      disabled: false
    }
  }),
  newOdds: cva2([""], {
    variants: {
      isClicked: {
        true: "text-black",
        false: "text-accent-secondary"
      },
      disabled: {
        true: "text-dark-grey-1",
        false: ""
      }
    },
    defaultVariants: {
      isClicked: false,
      disabled: false
    }
  })
};

// src/components/buttons/odds-boost-button/odds-boost-button.tsx
import { jsx as jsx11, jsxs as jsxs5 } from "react/jsx-runtime";
var OddsBoostButton = (props) => {
  const {
    oldOdds,
    newOdds,
    onClick,
    className,
    disabled,
    disabledText,
    isSelected
  } = props;
  return /* @__PURE__ */ jsx11(
    ToggleButton,
    {
      onClick,
      disabled,
      className,
      layout: "horizontal",
      disabledText,
      isSelected,
      children: /* @__PURE__ */ jsx11(ToggleButton.Content, { children: ({ isSelected: isSelected2, disabled: disabled2 }) => /* @__PURE__ */ jsxs5("div", { className: oddsBoostButtonVariants.oddsDisplay, children: [
        /* @__PURE__ */ jsx11("span", { className: oddsBoostButtonVariants.oldOdds, children: formatOdds(oldOdds) }),
        /* @__PURE__ */ jsx11(
          "span",
          {
            className: oddsBoostButtonVariants.arrow({
              isClicked: isSelected2,
              disabled: disabled2
            }),
            children: "\xBB"
          }
        ),
        /* @__PURE__ */ jsx11(
          "span",
          {
            className: oddsBoostButtonVariants.newOdds({
              isClicked: isSelected2,
              disabled: disabled2
            }),
            children: formatOdds(newOdds)
          }
        )
      ] }) })
    }
  );
};

// src/components/buttons/odds-boost-button/odds-boost-button.hook.tsx
import { useCallback as useCallback4, useState as useState3 } from "react";
var useOddsBoostButton = (props) => {
  const { oldOdds, newOdds, onClick, className, disabled = false } = props;
  const [isClicked, setIsClicked] = useState3(false);
  const handleClick = useCallback4(
    (e) => {
      if (disabled) return;
      setIsClicked((prev) => !prev);
      if (onClick) {
        onClick(e);
      }
    },
    [onClick, disabled]
  );
  return {
    oldOdds,
    newOdds,
    onClick: handleClick,
    className,
    isClicked,
    disabled
  };
};

// src/components/buttons/utils/arrow-characters.utils.ts
var getArrowChar = (direction) => {
  return direction === "up" ? "\u25B2" : "\u25BC";
};

// src/components/buttons/odds-button/odds-button.styles.ts
import { cva as cva3 } from "class-variance-authority";
var oddsButtonVariants = {
  selectionNumber: "text-dark-grey-1 leading-none h-4 flex items-center text-xs font-normal",
  oddsContainer: cva3(
    [
      "leading-[16px] text-center h-4 flex items-center gap-1 text-xs font-semibold",
      "transition-colors duration-150 ease-in-out"
    ],
    {
      variants: {
        isClicked: {
          true: "text-black",
          false: "text-accent-secondary group-hover:text-accent-secondary/90"
        },
        disabled: {
          true: "text-dark-grey-1",
          false: ""
        }
      },
      defaultVariants: {
        isClicked: false,
        disabled: false
      }
    }
  ),
  // Temporary arrow styles until we have icons package available
  arrow: cva3(["text-xs font-bold ml-1"], {
    variants: {
      direction: {
        up: "text-arrow-up",
        down: "text-arrow-down"
      }
    }
  })
};

// src/components/buttons/odds-button/odds-button.tsx
import { Fragment as Fragment2, jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
var OddsButton = (props) => {
  const {
    selectionNumber,
    odds,
    arrowDirection,
    onClick,
    className,
    disabled,
    disabledText,
    isSelected
  } = props;
  const formattedOdds = formatOdds(odds);
  return /* @__PURE__ */ jsx12(
    ToggleButton,
    {
      onClick,
      disabled,
      className,
      disabledText,
      isSelected,
      children: /* @__PURE__ */ jsx12(ToggleButton.Content, { children: ({ isSelected: isSelected2, disabled: disabled2 }) => /* @__PURE__ */ jsxs6(Fragment2, { children: [
        /* @__PURE__ */ jsx12("div", { className: oddsButtonVariants.selectionNumber, children: selectionNumber }),
        /* @__PURE__ */ jsxs6(
          "div",
          {
            className: oddsButtonVariants.oddsContainer({
              isClicked: isSelected2,
              disabled: disabled2
            }),
            children: [
              arrowDirection && /* @__PURE__ */ jsx12(
                "span",
                {
                  className: oddsButtonVariants.arrow({
                    direction: arrowDirection
                  }),
                  children: getArrowChar(arrowDirection)
                }
              ),
              formattedOdds
            ]
          }
        )
      ] }) })
    }
  );
};

// src/components/buttons/odds-button/odds-button.hook.tsx
import { useCallback as useCallback5, useState as useState4 } from "react";
var useOddsButton = (props) => {
  const {
    selectionNumber,
    odds,
    arrowDirection,
    onClick,
    className,
    disabled = false
  } = props;
  const [isClicked, setIsClicked] = useState4(false);
  const handleClick = useCallback5(
    (e) => {
      if (disabled) return;
      setIsClicked((prev) => !prev);
      if (onClick) {
        onClick(e);
      }
    },
    [onClick, disabled]
  );
  return {
    selectionNumber,
    odds,
    arrowDirection,
    onClick: handleClick,
    className,
    isClicked,
    disabled
  };
};

// src/components/cards/sports-card.tsx
import { EVENT_NAMES, useEventTracking } from "@repo/tracking";
import { useTranslation as useTranslation2 } from "@repo/translation";
import { useCallback as useCallback7, useMemo as useMemo3 } from "react";

// src/kambi-wapi/hooks/use-kambi-notifications.hook.ts
import { useCallback as useCallback6, useEffect as useEffect2, useRef } from "react";

// src/kambi-wapi/constants/notifications.const.ts
var KAMBI_NOTIFICATION_EVENT = "kambi-notification";

// src/kambi-wapi/types/notifications.types.ts
var KAMBI_EVENTS = {
  CASHED_IN: "cashedIn",
  PLACED_BET: "placedBet"
};

// src/kambi-wapi/hooks/use-kambi-notifications.hook.ts
var useKambiNotifications = (handlers) => {
  const handlersRef = useRef(handlers);
  const isListeningRef = useRef(false);
  useEffect2(() => {
    handlersRef.current = handlers;
  }, [handlers]);
  const handleKambiEvent = useCallback6((event) => {
    if (!event || !("detail" in event)) return;
    const kambiEvent = event;
    const { name } = kambiEvent.detail;
    switch (name) {
      case KAMBI_EVENTS.CASHED_IN:
        handlersRef.current.onCashedIn?.();
        break;
      case KAMBI_EVENTS.PLACED_BET:
        handlersRef.current.onPlacedBet?.();
        break;
    }
  }, []);
  useEffect2(() => {
    if (typeof window === "undefined" || isListeningRef.current) return;
    isListeningRef.current = true;
    window.addEventListener(KAMBI_NOTIFICATION_EVENT, handleKambiEvent);
    return () => {
      isListeningRef.current = false;
      window.removeEventListener(KAMBI_NOTIFICATION_EVENT, handleKambiEvent);
    };
  }, [handleKambiEvent]);
  return {};
};

// src/kambi-wapi/hooks/use-kambi-wapi.hook.ts
import { useMemo } from "react";

// src/kambi-wapi/hooks/use-kambi-api-initialiser.hook.ts
import { useEffect as useEffect3, useState as useState5 } from "react";

// src/kambi-wapi/constants/kambi-error-messages.const.ts
var ERROR_MESSAGES = {
  KAMBI_WIDGET_NOT_FOUND: "KambiWidget.ready was not found on window",
  UNKNOWN_INITIALISATION_ERROR: "Unknown error initialising Kambi Widget API",
  FAILED_INITIALISATION_PREFIX: "Failed to initialise Kambi Widget API: ",
  FAILED_TO_LOAD_WIDGET_API: "Failed to load Kambi Widget API"
};

// src/kambi-wapi/hooks/use-kambi-api-initialiser.hook.ts
var useKambiApiInitialiser = (isScriptLoaded) => {
  const [kambiApi, setKambiApi] = useState5(null);
  const [apiError, setApiError] = useState5(null);
  useEffect3(() => {
    let cancelled = false;
    if (!isScriptLoaded || typeof window === "undefined") {
      return;
    }
    const ready = window?.KambiWidget?.ready;
    if (!ready) {
      setApiError(ERROR_MESSAGES.KAMBI_WIDGET_NOT_FOUND);
      return;
    }
    const initialiseKambiApi = async () => {
      try {
        const api = await ready;
        if (cancelled) return;
        setKambiApi(api);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : ERROR_MESSAGES.UNKNOWN_INITIALISATION_ERROR;
        setApiError(`${ERROR_MESSAGES.FAILED_INITIALISATION_PREFIX}${msg}`);
      }
    };
    initialiseKambiApi();
    return () => {
      cancelled = true;
    };
  }, [isScriptLoaded]);
  return {
    kambiApi,
    apiError
  };
};

// src/kambi-wapi/hooks/use-kambi-script-loader.hook.ts
import { useEffect as useEffect4, useState as useState6 } from "react";

// src/kambi-wapi/env/kambi-wapi-url.ts
import { EnvConfig } from "@repo/env-config";
function getKambiApiUrl() {
  const url = EnvConfig.GATSBY_KAMBI_WIDGET_API;
  if (!url)
    throw new Error("EnvConfig.GATSBY_KAMBI_WIDGET_API is required but missing");
  return url;
}

// src/kambi-wapi/utils/load-external-script.ts
var scriptPromises = /* @__PURE__ */ new Map();
function loadExternalScript(src, attrs) {
  if (scriptPromises.has(src)) {
    const existingPromise = scriptPromises.get(src);
    if (existingPromise) return existingPromise;
  }
  const p = new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Cannot load scripts during server-side rendering"));
      return;
    }
    const existing = document.querySelector(
      `script[src="${src}"]`
    );
    if (existing && existing._loaded) {
      resolve(existing);
      return;
    }
    if (existing) {
      existing.addEventListener("load", () => resolve(existing));
      existing.addEventListener(
        "error",
        () => reject(new Error(`Failed to load script: ${src}`))
      );
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    if (attrs) Object.assign(s, attrs);
    s.onload = () => {
      ;
      s._loaded = true;
      resolve(s);
    };
    s.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(s);
  });
  scriptPromises.set(src, p);
  return p;
}

// src/kambi-wapi/hooks/use-kambi-script-loader.hook.ts
var useKambiScriptLoader = () => {
  const [isScriptLoaded, setIsScriptLoaded] = useState6(false);
  const [scriptError, setScriptError] = useState6(null);
  useEffect4(() => {
    let cancelled = false;
    if (typeof window === "undefined") return;
    if (window.KambiWidget) {
      setIsScriptLoaded(true);
      return;
    }
    const loadScript = async () => {
      try {
        const apiUrl = getKambiApiUrl();
        await loadExternalScript(apiUrl);
        if (!cancelled) {
          setIsScriptLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          setScriptError(
            err instanceof Error ? err.message : ERROR_MESSAGES.FAILED_TO_LOAD_WIDGET_API
          );
        }
      }
    };
    loadScript();
    return () => {
      cancelled = true;
    };
  }, []);
  return {
    isScriptLoaded,
    scriptError
  };
};

// src/kambi-wapi/hooks/use-kambi-wapi.hook.ts
function useKambiWebApi() {
  const { isScriptLoaded, scriptError } = useKambiScriptLoader();
  const { kambiApi, apiError } = useKambiApiInitialiser(isScriptLoaded);
  const error = scriptError || apiError;
  const isReady = Boolean(isScriptLoaded && kambiApi);
  const isLoading = !isScriptLoaded && !error;
  return useMemo(
    () => ({
      kambiApi,
      isReady,
      isLoading,
      error
    }),
    [kambiApi, isReady, isLoading, error]
  );
}

// src/components/sports-market/sports-market.tsx
import { forwardRef as forwardRef8 } from "react";

// src/components/sports-market/sports-market.styles.ts
import { cva as cva4 } from "class-variance-authority";
var sportsMarketVariants = cva4(
  "flex items-center justify-between w-full"
);
var sportsMarketTypeVariants = cva4(
  "text-dark-grey-1 text-xs font-normal leading-3 h-3"
);
var sportsMarketSignpostingVariants = cva4("flex items-center gap-1");
var sportsMarketSignpostingIconVariants = cva4("w-4 h-4");
var sportsMarketSignpostingLabelVariants = cva4(
  "text-white text-xs font-normal"
);

// src/components/sports-market/sports-market.tsx
import { jsx as jsx13, jsxs as jsxs7 } from "react/jsx-runtime";
var SportsMarket = forwardRef8(({ data, className, ...props }, ref) => {
  const { marketType, signposting } = data;
  return /* @__PURE__ */ jsxs7("div", { ref, className: sportsMarketVariants({ className }), ...props, children: [
    /* @__PURE__ */ jsx13("div", { className: sportsMarketTypeVariants(), children: marketType }),
    /* @__PURE__ */ jsx13("div", { className: "flex gap-1 leading-3", children: signposting?.map((signpost, index) => /* @__PURE__ */ jsxs7(
      "div",
      {
        className: sportsMarketSignpostingVariants(),
        children: [
          /* @__PURE__ */ jsx13("div", { className: sportsMarketSignpostingIconVariants(), children: renderIcon(signpost.icon) }),
          /* @__PURE__ */ jsx13(
            "div",
            {
              className: sportsMarketSignpostingLabelVariants(),
              style: { color: signpost.color || "white" },
              children: signpost.label
            }
          )
        ]
      },
      `signpost-${signpost.label}-${index}`
    )) })
  ] });
});
SportsMarket.displayName = "SportsMarket";

// src/components/sports-odds-set/sports-odds-set.tsx
import { forwardRef as forwardRef9 } from "react";

// src/components/sports-odds-set/sports-odds-set.styles.ts
import { cva as cva5 } from "class-variance-authority";
var sportsOddsSetContainerVariants = cva5("flex gap-1 w-full", {
  variants: {
    state: {
      default: "",
      disabled: "items-center justify-center"
    }
  },
  defaultVariants: {
    state: "default"
  }
});
var sportsOddsSetDisabledOddsVariants = cva5(
  "bg-dark-grey-5 flex flex-col items-center justify-center min-h-10 p-1 rounded-md w-full border border-dark-grey-3"
);
var sportsOddsSetDisabledOddsTextVariants = cva5(
  "text-dark-grey-1 text-xs font-normal text-center"
);

// src/components/sports-odds-set/sports-odds-set.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
var SportsOddsSet = forwardRef9(
  ({
    data,
    disabledState,
    className,
    messages,
    onOddsClick,
    betslipState,
    ...props
  }, ref) => {
    const { odds } = data;
    if (disabledState) {
      const disabledMessage = disabledState === "unavailable-market" ? messages.unavailableMarket : messages.inactiveEvent;
      return /* @__PURE__ */ jsx14(
        "div",
        {
          ref,
          className: sportsOddsSetContainerVariants({
            state: "disabled",
            className
          }),
          ...props,
          children: /* @__PURE__ */ jsx14("div", { className: sportsOddsSetDisabledOddsVariants(), children: /* @__PURE__ */ jsx14("div", { className: sportsOddsSetDisabledOddsTextVariants(), children: disabledMessage }) })
        }
      );
    }
    return /* @__PURE__ */ jsx14(
      "div",
      {
        ref,
        className: sportsOddsSetContainerVariants({ className }),
        ...props,
        children: odds.map((odd, index) => {
          const isInBetslip = betslipState?.outcomes.some(
            (outcome) => outcome.id === odd.id
          );
          return /* @__PURE__ */ jsx14(
            "div",
            {
              className: "flex-1",
              children: odd.isBoosted && odd.oldValue ? /* @__PURE__ */ jsx14(
                OddsBoostButton,
                {
                  oldOdds: odd.oldValue,
                  newOdds: odd.value,
                  className: "w-full",
                  isSelected: isInBetslip,
                  onClick: (e) => {
                    e.stopPropagation();
                    onOddsClick?.(odd);
                  }
                }
              ) : /* @__PURE__ */ jsx14(
                OddsButton,
                {
                  selectionNumber: odd.label,
                  odds: odd.value,
                  arrowDirection: odd.arrowDirection,
                  className: "w-full",
                  isSelected: isInBetslip,
                  onClick: (e) => {
                    e.stopPropagation();
                    onOddsClick?.(odd);
                  }
                }
              )
            },
            `odds-${odd.label}-${odd.value}-${index}`
          );
        })
      }
    );
  }
);
SportsOddsSet.displayName = "SportsOddsSet";

// src/components/sports-participants/sports-participants.tsx
import { forwardRef as forwardRef10 } from "react";

// src/components/sports-participants/sports-participants.styles.ts
import { cva as cva6 } from "class-variance-authority";
var sportsParticipantsVariants = cva6("flex flex-col gap-1 w-full");
var sportsParticipantsContainerVariants = cva6(
  "flex flex-row justify-between py-1"
);
var sportsParticipantsListVariants = cva6(
  "flex flex-col gap-0.5 text-base font-normal leading-5",
  {
    variants: {
      disabledState: {
        "unavailable-market": "text-white",
        "inactive-event": "text-dark-grey-3"
      }
    },
    defaultVariants: {
      disabledState: "unavailable-market"
    }
  }
);
var sportsParticipantItemVariants = cva6("text-left");
var sportsParticipantsScoresVariants = cva6(
  "flex flex-col gap-0.5 items-start justify-center text-base font-normal leading-5 text-right",
  {
    variants: {
      isLive: {
        true: "text-live",
        false: "text-dark-grey-1"
      }
    },
    defaultVariants: {
      isLive: false
    }
  }
);

// src/components/sports-participants/sports-participants.tsx
import { jsx as jsx15, jsxs as jsxs8 } from "react/jsx-runtime";
var SportsParticipants = forwardRef10(({ data, disabledState, className, ...props }, ref) => {
  const { participants, scores, isLive } = data;
  return /* @__PURE__ */ jsx15(
    "div",
    {
      ref,
      className: sportsParticipantsVariants({ className }),
      ...props,
      children: /* @__PURE__ */ jsxs8("div", { className: sportsParticipantsContainerVariants(), children: [
        /* @__PURE__ */ jsxs8("div", { className: sportsParticipantsListVariants({ disabledState }), children: [
          /* @__PURE__ */ jsx15("div", { className: sportsParticipantItemVariants(), children: participants[0] }),
          /* @__PURE__ */ jsx15("div", { className: sportsParticipantItemVariants(), children: participants[1] })
        ] }),
        isLive && scores && /* @__PURE__ */ jsxs8("div", { className: sportsParticipantsScoresVariants({ isLive: true }), children: [
          /* @__PURE__ */ jsx15("div", { children: scores[0] }),
          /* @__PURE__ */ jsx15("div", { children: scores[1] })
        ] })
      ] })
    }
  );
});
SportsParticipants.displayName = "SportsParticipants";

// src/components/sports-time-league/sports-time-league.tsx
import { useTranslation } from "@repo/translation";
import { useMemo as useMemo2 } from "react";

// src/components/sports-time-league/sports-time-league.styles.ts
import { cva as cva7 } from "class-variance-authority";
var sportsTimeLeagueVariants = cva7(
  "flex items-start gap-2 w-full  leading-2.5"
);
var sportsTimeLeagueTimeVariants = cva7("text-xs font-normal", {
  variants: {
    isLive: {
      true: "text-live",
      false: "text-dark-grey-1"
    }
  },
  defaultVariants: {
    isLive: false
  }
});
var sportsTimeLeagueSportIconVariants = cva7(
  "flex items-center justify-center w-3 h-3"
);
var sportsTimeLeagueLeagueVariants = cva7(
  "text-dark-grey-1 text-xs font-normal flex items-center gap-1 ml-auto h-4"
);

// src/components/sports-time-league/sports-time-league.tsx
import { jsx as jsx16, jsxs as jsxs9 } from "react/jsx-runtime";
var SportsTimeLeague = ({
  data,
  className,
  ...props
}) => {
  const { translate } = useTranslation();
  const { time, sportType, league, isLive, matchTime, isPinned } = data;
  const displayTime = useMemo2(
    () => isLive ? `${translate("topEvents.match.live")} ${matchTime || ""}` : time,
    [translate, isLive, matchTime, time]
  );
  return /* @__PURE__ */ jsxs9("div", { className: sportsTimeLeagueVariants({ className }), ...props, children: [
    isPinned && /* @__PURE__ */ jsx16("div", { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx16(Pin, { className: "w-3 h-3" }) }),
    /* @__PURE__ */ jsx16("div", { className: sportsTimeLeagueTimeVariants({ isLive }), children: displayTime }),
    /* @__PURE__ */ jsxs9("div", { className: sportsTimeLeagueLeagueVariants(), children: [
      league && /* @__PURE__ */ jsx16("div", { className: sportsTimeLeagueSportIconVariants(), children: renderIcon(getSportIcon(sportType)) }),
      league
    ] })
  ] });
};

// src/components/cards/sports-card.styles.ts
import { cva as cva8 } from "class-variance-authority";
var sportsCardVariants = cva8(
  "flex flex-col gap-1 bg-dark-grey-5 p-2 rounded-xl w-[304px]"
);

// src/components/cards/sports-card-skeleton/sports-card-skeleton.tsx
import { Skeleton } from "@repo/ui";
import { jsx as jsx17, jsxs as jsxs10 } from "react/jsx-runtime";
var OddsSkeleton = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx17(
  "div",
  {
    className: `animate-pulse rounded-sm bg-dark-grey-3 ${className}`,
    ...props
  }
);
var SportsCardSkeleton = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsxs10(
    "div",
    {
      className: `flex flex-col gap-1 bg-dark-grey-5 p-2 rounded-xl w-[304px] ${className || ""}`,
      ...props,
      children: [
        /* @__PURE__ */ jsxs10("div", { className: "flex items-center gap-2 w-full", children: [
          /* @__PURE__ */ jsx17(Skeleton, { className: "h-3 rounded-sm w-[60px]" }),
          /* @__PURE__ */ jsx17(Skeleton, { className: "h-3 rounded-sm w-[135px] ml-auto" })
        ] }),
        /* @__PURE__ */ jsxs10("div", { className: "flex flex-col gap-1 w-full", children: [
          /* @__PURE__ */ jsx17(Skeleton, { className: "h-5 rounded-sm w-full" }),
          /* @__PURE__ */ jsx17(Skeleton, { className: "h-5 rounded-sm w-[60px]" })
        ] }),
        /* @__PURE__ */ jsxs10("div", { className: "flex items-center justify-between w-full", children: [
          /* @__PURE__ */ jsx17(Skeleton, { className: "h-4 rounded-sm w-[109px]" }),
          /* @__PURE__ */ jsxs10("div", { className: "flex gap-1", children: [
            /* @__PURE__ */ jsx17(Skeleton, { className: "h-4 rounded-sm w-4" }),
            /* @__PURE__ */ jsx17(Skeleton, { className: "h-4 rounded-sm w-4" })
          ] })
        ] }),
        /* @__PURE__ */ jsx17("div", { className: "flex gap-1 w-full", children: ["first", "second", "third"].map((position) => /* @__PURE__ */ jsx17(
          "div",
          {
            className: "bg-dark-grey-4 flex flex-col items-center justify-center min-h-10 p-1 rounded-md flex-1",
            children: /* @__PURE__ */ jsxs10("div", { className: "flex flex-col gap-1 items-center justify-start w-full", children: [
              /* @__PURE__ */ jsx17(OddsSkeleton, { className: "h-3 rounded-sm w-[34px]" }),
              /* @__PURE__ */ jsx17(OddsSkeleton, { className: "h-3 rounded-sm w-[62px]" })
            ] })
          },
          position
        )) })
      ]
    }
  );
};

// src/components/cards/sports-card.tsx
import { jsx as jsx18, jsxs as jsxs11 } from "react/jsx-runtime";
var SportsCard = ({
  eventData,
  eventId,
  disabledState,
  isLoading,
  className,
  messages,
  onClick,
  cardRank,
  ...props
}) => {
  const { translate } = useTranslation2();
  const { trackEvent } = useEventTracking();
  const defaultMessages = useMemo3(
    () => ({
      unavailableMarket: translate("topEvents.error.notAvailable") || "",
      inactiveEvent: translate("topEvents.error.tempUnavailable") || ""
    }),
    [translate]
  );
  const finalMessages = messages || defaultMessages;
  const { kambiApi } = useKambiWebApi();
  const { betslipState, toggleOutcome, isOutcomeInBetslip } = useBetslipWithState(kambiApi);
  const handleOddsClick = useCallback7(
    (oddsData) => {
      if (!kambiApi) return;
      const outcome = {
        id: oddsData.id,
        label: oddsData.label,
        odds: Number.parseFloat(oddsData.value),
        betofferId: oddsData.betofferId || 0,
        eventId
      };
      const isCurrentlyInBetslip = isOutcomeInBetslip(oddsData.id);
      const actionTaken = isCurrentlyInBetslip ? "selection_removed" : "selection_added";
      if (trackEvent && eventData) {
        trackEvent(
          EVENT_NAMES.CARD_ACTION_TAKEN,
          {
            widget_name: "top_events",
            card_rank: cardRank,
            event_id: eventId,
            event_name: eventData.participants?.join(" vs ") || "",
            selection_id: oddsData.id,
            selection_name: oddsData.label,
            odds: oddsData.value,
            site_locaiton: window.location.pathname,
            action_taken: actionTaken
          },
          true
        );
      }
      toggleOutcome(outcome);
    },
    [
      kambiApi,
      toggleOutcome,
      isOutcomeInBetslip,
      eventId,
      trackEvent,
      eventData,
      cardRank
    ]
  );
  if (isLoading || !eventData) {
    return /* @__PURE__ */ jsx18(SportsCardSkeleton, { className });
  }
  const {
    time,
    league,
    sportType,
    participants,
    marketType,
    signposting,
    odds,
    isLive,
    matchTime,
    scores,
    isPinned,
    onClick: eventOnClick
  } = eventData;
  return /* @__PURE__ */ jsxs11(
    "div",
    {
      className: sportsCardVariants({ className }),
      onClick: eventOnClick || onClick,
      role: "button",
      tabIndex: 0,
      ...props,
      children: [
        /* @__PURE__ */ jsx18(
          SportsTimeLeague,
          {
            data: {
              time,
              sportType,
              league,
              isLive,
              matchTime,
              isPinned
            }
          }
        ),
        /* @__PURE__ */ jsx18(
          SportsParticipants,
          {
            data: {
              participants,
              scores,
              isLive
            },
            disabledState
          }
        ),
        /* @__PURE__ */ jsx18(
          SportsMarket,
          {
            data: {
              marketType,
              signposting
            }
          }
        ),
        /* @__PURE__ */ jsx18(
          SportsOddsSet,
          {
            data: {
              odds
            },
            disabledState,
            messages: finalMessages,
            onOddsClick: handleOddsClick,
            betslipState
          }
        )
      ]
    }
  );
};

// src/components/top-events/top-event-card.tsx
import { useEventTracking as useEventTracking2 } from "@repo/tracking";
import { useTranslation as useTranslation3 } from "@repo/translation";
import { memo, useMemo as useMemo4 } from "react";

// src/store/sportsbook-store.ts
import { createSelectorHooks } from "auto-zustand-selectors-hook";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
var initialState = {
  topEvents: /* @__PURE__ */ new Map(),
  events: /* @__PURE__ */ new Map(),
  betOffers: /* @__PURE__ */ new Map(),
  prePacks: /* @__PURE__ */ new Map(),
  range: null
};
var useSportsbookStoreBase = create()(
  devtools(
    (set, _get) => ({
      ...initialState,
      // Top Events actions
      setTopEvents: (events) => set({
        topEvents: new Map(events.map((event) => [event.eventId, event]))
      }),
      addTopEvent: (event) => set((state) => {
        const newTopEvents = new Map(state.topEvents);
        newTopEvents.set(event.eventId, event);
        return { topEvents: newTopEvents };
      }),
      removeTopEvent: (eventId) => set((state) => {
        const newTopEvents = new Map(state.topEvents);
        newTopEvents.delete(eventId);
        return { topEvents: newTopEvents };
      }),
      clearTopEvents: () => set({ topEvents: /* @__PURE__ */ new Map() }),
      // Events actions
      setEvents: (events) => set({
        events: new Map(events.map((event) => [event.id, event]))
      }),
      addEvent: (event) => set((state) => {
        const newEvents = new Map(state.events);
        newEvents.set(event.id, event);
        return { events: newEvents };
      }),
      removeEvent: (id) => set((state) => {
        const newEvents = new Map(state.events);
        newEvents.delete(id);
        return { events: newEvents };
      }),
      clearEvents: () => set({ events: /* @__PURE__ */ new Map() }),
      updateEvent: (eventId, updates) => set((state) => {
        const event = state.events.get(eventId);
        if (!event) return state;
        const newEvents = new Map(state.events);
        newEvents.set(eventId, { ...event, ...updates });
        return { events: newEvents };
      }),
      // Bet Offers actions
      setBetOffers: (betOffers) => set({
        betOffers: new Map(betOffers.map((offer) => [offer.id, offer]))
      }),
      addBetOffer: (betOffer) => set((state) => {
        const newBetOffers = new Map(state.betOffers);
        newBetOffers.set(betOffer.id, betOffer);
        return { betOffers: newBetOffers };
      }),
      removeBetOffer: (id) => set((state) => {
        const newBetOffers = new Map(state.betOffers);
        newBetOffers.delete(id);
        return { betOffers: newBetOffers };
      }),
      clearBetOffers: () => set({ betOffers: /* @__PURE__ */ new Map() }),
      updateBetOffer: (offerId, updates) => set((state) => {
        const offer = state.betOffers.get(offerId);
        if (!offer) return state;
        const newBetOffers = new Map(state.betOffers);
        newBetOffers.set(offerId, { ...offer, ...updates });
        return { betOffers: newBetOffers };
      }),
      // Pre Packs actions
      setPrePacks: (prePacks) => set({
        prePacks: new Map(prePacks.map((prePack) => [prePack.id, prePack]))
      }),
      addPrePack: (prePack) => set((state) => {
        const newPrePacks = new Map(state.prePacks);
        newPrePacks.set(prePack.id, prePack);
        return { prePacks: newPrePacks };
      }),
      removePrePack: (id) => set((state) => {
        const newPrePacks = new Map(state.prePacks);
        newPrePacks.delete(id);
        return { prePacks: newPrePacks };
      }),
      clearPrePacks: () => set({ prePacks: /* @__PURE__ */ new Map() }),
      // Range actions
      setRange: (range) => set({ range }),
      // Reset action
      reset: () => set(initialState)
    }),
    {
      name: "sportsbook-store"
    }
  )
);
var useSportsbookStore = createSelectorHooks(
  useSportsbookStoreBase
);
var sportsbookStore = useSportsbookStoreBase;
var sportsbookSelectors = {
  getEventsBySport: (sport) => (state) => Array.from(state.events.values()).filter((event) => event.sport === sport),
  getEventsByState: (eventState) => (state) => Array.from(state.events.values()).filter(
    (event) => event.state === eventState
  ),
  getEventsByName: (name) => (state) => Array.from(state.events.values()).filter(
    (event) => event.name.toLowerCase().includes(name.toLowerCase())
  ),
  getBetOffersByEventId: (eventId) => (state) => Array.from(state.betOffers.values()).filter(
    (offer) => offer.eventId === eventId
  ),
  getBetOffersByType: (betOfferTypeId) => (state) => Array.from(state.betOffers.values()).filter(
    (offer) => offer.betOfferType?.id === betOfferTypeId
  ),
  getBetOffersBySuspended: (suspended) => (state) => Array.from(state.betOffers.values()).filter(
    (offer) => offer.suspended === suspended
  )
};

// src/components/top-events/top-events.utils.ts
import { EVENT_NAMES as EVENT_NAMES2 } from "@repo/tracking";
var transformToSportsCardData = (event, kambiApi, _translate, trackEvent, cardRank) => {
  const { eventData } = event;
  const allBetOffers = eventData?.betoffers || [];
  const mainBetOffers = allBetOffers.filter(
    (betOffer) => !betOffer.suspended && betOffer.cashOutStatus !== "closed"
  );
  const participants = event.eventName.split(" - ");
  const odds = (() => {
    const initialBetOffers = eventData?.initialBetoffers || [];
    return mainBetOffers.slice(0, 3).flatMap(
      (betOffer) => betOffer.outcomes?.slice(0, 3).map((outcome) => {
        const initialOutcome = initialBetOffers.find((initialOffer) => initialOffer.id === betOffer.id)?.outcomes?.find(
          (initialOutcome2) => initialOutcome2.id === outcome.id
        );
        const currentOdds = outcome.odds.toString();
        const initialOdds = initialOutcome?.odds.toString();
        const arrowDirection = (() => {
          if (!initialOdds || initialOdds === currentOdds) return void 0;
          const current = Number.parseFloat(currentOdds);
          const initial = Number.parseFloat(initialOdds);
          return current > initial ? "up" : "down";
        })();
        const oddsChanged = initialOdds && initialOdds !== currentOdds;
        return {
          id: outcome.id,
          label: outcome.label,
          value: currentOdds,
          isBoosted: false,
          betofferId: betOffer.id,
          ...oddsChanged && { oldValue: initialOdds },
          ...arrowDirection && { arrowDirection }
        };
      }) || []
    );
  })();
  const formatTime = () => {
    if (eventData?.formattedStartTime && eventData?.formattedStartDate) {
      return `${eventData.formattedStartDate} ${eventData.formattedStartTime}`;
    }
    return "";
  };
  const league = eventData?.path?.slice(-2).map((path) => path.name).join(" / ") || "";
  const scores = [];
  if (eventData?.score) {
    scores.push(eventData.score.home, eventData.score.away);
  }
  return {
    time: formatTime(),
    league,
    sportType: eventData?.sport?.toLowerCase(),
    participants,
    marketType: (() => {
      if (mainBetOffers.length > 0) {
        return mainBetOffers[0]?.criterion?.label || "";
      }
      return allBetOffers[0]?.criterion?.label || "";
    })(),
    signposting: [
      // {
      //   icon: <FomoM className="w-4 h-4" />,
      //   label: `${event.totalBets} ${translate('topEvents.fomo.placed')}`,
      //   color: '#FFD700',
      // },
    ],
    odds,
    isLive: eventData?.isLive,
    isPinned: false,
    scores,
    url: eventData?.url,
    onClick: (() => {
      if (eventData?.url && kambiApi?.navigateClient) {
        const url = eventData.url;
        return () => {
          if (trackEvent) {
            trackEvent(
              EVENT_NAMES2.CARD_TAPPED,
              {
                widget_name: "top_events",
                card_rank: cardRank,
                event_id: event.kambiEventId,
                event_name: event.eventName,
                site_locaiton: window.location.pathname
              },
              true
            );
          }
          kambiApi.navigateClient(url);
        };
      }
      return void 0;
    })()
  };
};

// src/components/top-events/top-event-card.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var TopEventCardComponent = ({
  topEvent,
  cardIndex,
  className
}) => {
  const { translate } = useTranslation3();
  const { trackEvent } = useEventTracking2();
  const { kambiApi } = useKambiWebApi();
  const eventData = useSportsbookStore.useEvents().get(topEvent.kambiEventId);
  const enrichedEvent = useMemo4(
    () => ({
      ...topEvent,
      eventData,
      betOffers: eventData?.betoffers ?? []
    }),
    [topEvent, eventData]
  );
  const sportsCardData = useMemo4(() => {
    return transformToSportsCardData(
      enrichedEvent,
      kambiApi,
      translate,
      trackEvent,
      cardIndex + 1
    );
  }, [enrichedEvent, kambiApi, translate, trackEvent, cardIndex]);
  const disabledState = useMemo4(() => {
    const hasBetOffers = (eventData?.betoffers?.length || 0) > 0;
    const hasEventUrl = !!sportsCardData.url;
    return hasEventUrl ? hasBetOffers ? null : "unavailable-market" : "inactive-event";
  }, [eventData?.betoffers?.length, sportsCardData.url]);
  const isLoading = !eventData?.info;
  return /* @__PURE__ */ jsx19(
    SportsCard,
    {
      isLoading,
      eventData: sportsCardData,
      eventId: topEvent.eventId,
      disabledState,
      className,
      cardRank: cardIndex + 1
    }
  );
};
var TopEventCard = memo(TopEventCardComponent);

// src/components/top-events/top-events.tsx
import { EmblaCarousel as EmblaCarousel2 } from "@repo/ui";
import { useMemo as useMemo5 } from "react";

// src/providers/translation-provider.tsx
import {
  TranslationProvider as TranslationProviderBase,
  useTranslation as useTranslation4
} from "@repo/translation";
import { jsx as jsx20 } from "react/jsx-runtime";
var TranslationProvider = ({
  children,
  loadJson
}) => {
  return /* @__PURE__ */ jsx20(TranslationProviderBase, { loadJson, children });
};

// src/components/loading-carousel/loading-carousel.tsx
import { EmblaCarousel } from "@repo/ui";
import { jsx as jsx21 } from "react/jsx-runtime";
var LoadingCarousel = ({
  className = "",
  skeletonCount = 5
}) => {
  return /* @__PURE__ */ jsx21(
    EmblaCarousel,
    {
      options: {
        align: "start",
        skipSnaps: true,
        containScroll: "trimSnaps"
      },
      className: `w-full h-auto ${className}`,
      children: Array.from({ length: skeletonCount }, (_, index) => /* @__PURE__ */ jsx21(
        EmblaCarousel.Slide,
        {
          className: "pr-4",
          children: /* @__PURE__ */ jsx21(SportsCardSkeleton, { className: "h-full" })
        },
        `loading-skeleton-${index + 1}`
      ))
    }
  );
};

// src/components/top-events/top-events.tsx
import { jsx as jsx22, jsxs as jsxs12 } from "react/jsx-runtime";
var TopEvents = () => {
  const { translate } = useTranslation4();
  const topEventsMap = useSportsbookStore.useTopEvents();
  const topEvents = useMemo5(
    () => Array.from(topEventsMap.values()),
    [topEventsMap]
  );
  return /* @__PURE__ */ jsxs12("div", { className: "mx-auto", children: [
    /* @__PURE__ */ jsxs12("span", { className: "flex items-center gap-1 text-lg font-['Inter:Bold',_sans-serif] font-bold text-white leading-10", children: [
      /* @__PURE__ */ jsx22(Heading, { className: "w-6 h-6" }),
      translate("topEvents.heading")
    ] }),
    topEvents.length === 0 ? /* @__PURE__ */ jsx22(LoadingCarousel, {}) : /* @__PURE__ */ jsx22(
      EmblaCarousel2,
      {
        options: {
          align: "start",
          skipSnaps: true,
          containScroll: "trimSnaps"
        },
        className: "w-full h-auto",
        children: topEvents.map((topEvent, index) => /* @__PURE__ */ jsx22(EmblaCarousel2.Slide, { className: "pr-4", children: /* @__PURE__ */ jsx22(
          TopEventCard,
          {
            topEvent,
            cardIndex: index,
            className: "h-full"
          }
        ) }, topEvent.eventId))
      }
    )
  ] });
};

// src/components/top-events/top-events.container.tsx
import { GrowthBookProvider } from "@repo/growthbook";
import { EventTrackingProvider } from "@repo/tracking";

// src/components/top-events/top-events-engine.tsx
import { getTopEvents } from "@repo/core-api";
import { useCallback as useCallback13, useEffect as useEffect5, useMemo as useMemo6, useState as useState7 } from "react";

// src/event-data/hooks/use-event-data.hook.ts
import { useCallback as useCallback8 } from "react";

// src/event-data/utils/event-data-api.utils.ts
var requestEventData = (api, eventId, infoType = api.EVENT_INFO_TYPES.BET_OFFERS, mainOnly = true) => {
  const payload = {
    id: eventId,
    info: infoType,
    mainOnly
  };
  api.request(api.EVENT_INFO, payload);
};
var unsubscribeEventData = (api, eventId) => {
  api.request(api.EVENT_INFO_UNSUBSCRIBE, { id: eventId });
};

// src/event-data/hooks/use-event-data.hook.ts
var useEventData = (kambiApi) => {
  const requestEventData2 = useCallback8(
    (eventId, infoType, mainOnly) => {
      if (!kambiApi) return;
      requestEventData(kambiApi, eventId, infoType, mainOnly);
    },
    [kambiApi]
  );
  const unsubscribeEventData2 = useCallback8(
    (eventId) => {
      if (!kambiApi) return;
      unsubscribeEventData(kambiApi, eventId);
    },
    [kambiApi]
  );
  return {
    requestEventData: requestEventData2,
    unsubscribeEventData: unsubscribeEventData2
  };
};

// src/hooks/use-bet-offers.hook.ts
import { useCallback as useCallback9 } from "react";
var useBetOffersActions = () => {
  return {
    setBetOffers: useSportsbookStore.useSetBetOffers(),
    addBetOffer: useSportsbookStore.useAddBetOffer(),
    removeBetOffer: useSportsbookStore.useRemoveBetOffer(),
    clearBetOffers: useSportsbookStore.useClearBetOffers(),
    updateBetOffer: useSportsbookStore.useUpdateBetOffer()
  };
};
var useBetOffer = (offerId) => {
  return useSportsbookStore((state) => state.betOffers.get(offerId));
};
var useUpdateBetOffer = () => {
  const updateBetOffer = useSportsbookStore.useUpdateBetOffer();
  return useCallback9(
    (offerId, updates) => {
      updateBetOffer(offerId, updates);
    },
    [updateBetOffer]
  );
};

// src/hooks/use-events.hook.ts
import { useCallback as useCallback10 } from "react";
var useEventsActions = () => {
  return {
    setEvents: useSportsbookStore.useSetEvents(),
    addEvent: useSportsbookStore.useAddEvent(),
    removeEvent: useSportsbookStore.useRemoveEvent(),
    clearEvents: useSportsbookStore.useClearEvents(),
    updateEvent: useSportsbookStore.useUpdateEvent()
  };
};
var useEvent = (eventId) => {
  return useSportsbookStore((state) => state.events.get(eventId));
};
var useUpdateEvent = () => {
  const updateEvent = useSportsbookStore.useUpdateEvent();
  return useCallback10(
    (eventId, updates) => {
      updateEvent(eventId, updates);
    },
    [updateEvent]
  );
};

// src/hooks/use-pre-packs.hook.ts
import { useCallback as useCallback11 } from "react";
var usePrePacksActions = () => {
  return {
    setPrePacks: useSportsbookStore.useSetPrePacks(),
    addPrePack: useSportsbookStore.useAddPrePack(),
    removePrePack: useSportsbookStore.useRemovePrePack(),
    clearPrePacks: useSportsbookStore.useClearPrePacks()
  };
};
var usePrePack = (prePackId) => {
  return useSportsbookStore(
    (state) => state.prePacks.get(prePackId)
  );
};
var useUpdatePrePack = () => {
  const addPrePack = useSportsbookStore.useAddPrePack();
  return useCallback11(
    (prePackId, updates) => {
      const currentPrePack = sportsbookStore.getState().prePacks.get(prePackId);
      if (!currentPrePack) return;
      const updatedPrePack = {
        ...currentPrePack,
        ...updates
      };
      addPrePack(updatedPrePack);
    },
    [addPrePack]
  );
};

// src/hooks/use-top-events.hook.ts
import { useCallback as useCallback12 } from "react";
var useTopEventsActions = () => {
  return {
    setTopEvents: useSportsbookStore.useSetTopEvents(),
    addTopEvent: useSportsbookStore.useAddTopEvent(),
    removeTopEvent: useSportsbookStore.useRemoveTopEvent(),
    clearTopEvents: useSportsbookStore.useClearTopEvents()
  };
};
var useTopEvent = (eventId) => {
  return useSportsbookStore(
    (state) => state.topEvents.get(eventId)
  );
};
var useUpdateTopEvent = () => {
  const addTopEvent = useSportsbookStore.useAddTopEvent();
  return useCallback12(
    (eventId, updates) => {
      const currentEvent = sportsbookStore.getState().topEvents.get(eventId);
      if (!currentEvent) return;
      const updatedEvent = {
        ...currentEvent,
        ...updates
      };
      addTopEvent(updatedEvent);
    },
    [addTopEvent]
  );
};

// src/components/top-events/top-events-engine.tsx
var TopEventsEngine = () => {
  const { kambiApi, isReady } = useKambiWebApi();
  const { requestEventData: requestEventData2, unsubscribeEventData: unsubscribeEventData2 } = useEventData(kambiApi);
  const { setTopEvents } = useTopEventsActions();
  const { addEvent, updateEvent } = useEventsActions();
  const topEventsMap = useSportsbookStore.useTopEvents();
  const topEvents = useMemo6(
    () => Array.from(topEventsMap.values()),
    [topEventsMap]
  );
  const fetchTopEvents = useCallback13(async () => {
    try {
      const response = await getTopEvents();
      setTopEvents(response.content);
    } catch (_error) {
    }
  }, [setTopEvents]);
  useEffect5(() => {
    fetchTopEvents();
  }, [fetchTopEvents]);
  const handleEventDataUpdate = useCallback13(
    (response) => {
      try {
        if (!response.data) {
          return;
        }
        if (response.type !== "EventInfo") {
          return;
        }
        let eventData = response.data;
        const eventId = typeof eventData.id === "number" ? eventData.id : Number.parseInt(
          String(eventData.id).replace("_context-card", ""),
          10
        );
        if (eventData.info === "MATCH_CLOCK") {
          eventData = {
            id: eventId,
            clock: eventData
          };
        } else if (eventData.info === "MATCH_OCCURENCES") {
          eventData = { ...eventData, id: eventId };
        } else if (eventData.info === "SCORE") {
          eventData = { id: eventId, score: eventData.score };
        } else if (eventData.info === "BET_OFFERS") {
          eventData = { ...eventData, id: eventId };
        }
        const currentEvents = useSportsbookStore.getState().events;
        const existingEvent = currentEvents.get(eventId);
        if (eventData.betoffers && !existingEvent?.initialBetoffers) {
          eventData.initialBetoffers = eventData.betoffers;
        }
        if (existingEvent) {
          updateEvent(eventId, eventData);
        } else {
          addEvent(eventData);
        }
      } catch (_error) {
      }
    },
    [addEvent, updateEvent]
  );
  useEffect5(() => {
    if (!isReady || !kambiApi) return;
    kambiApi.subscribe(handleEventDataUpdate);
  }, [isReady, kambiApi, handleEventDataUpdate]);
  const [hasRequestedEventData, setHasRequestedEventData] = useState7(false);
  useEffect5(() => {
    setHasRequestedEventData(false);
  }, [topEvents.length]);
  useEffect5(() => {
    if (!isReady || !kambiApi || topEvents.length === 0 || hasRequestedEventData)
      return;
    topEvents.forEach((topEvent) => {
      requestEventData2(
        topEvent.kambiEventId,
        kambiApi.EVENT_INFO_TYPES.BET_OFFERS,
        true
      );
      requestEventData2(
        topEvent.kambiEventId,
        kambiApi.EVENT_INFO_TYPES.MATCH_CLOCK,
        false
      );
      requestEventData2(
        topEvent.kambiEventId,
        kambiApi.EVENT_INFO_TYPES.MATCH_OCCURENCES,
        false
      );
      requestEventData2(
        topEvent.kambiEventId,
        kambiApi.EVENT_INFO_TYPES.SCORE,
        false
      );
    });
    setHasRequestedEventData(true);
  }, [
    isReady,
    kambiApi,
    topEvents.length,
    requestEventData2,
    hasRequestedEventData
  ]);
  useEffect5(() => {
    return () => {
      if (topEvents.length > 0) {
        topEvents.forEach((topEvent) => {
          unsubscribeEventData2(topEvent.kambiEventId);
        });
      }
    };
  }, [topEvents, unsubscribeEventData2]);
  return null;
};

// src/components/top-events/top-events.container.tsx
import { jsx as jsx23, jsxs as jsxs13 } from "react/jsx-runtime";
var TopEventsContainer = ({
  loadJson
}) => {
  return /* @__PURE__ */ jsx23(GrowthBookProvider, { children: /* @__PURE__ */ jsx23(EventTrackingProvider, { children: /* @__PURE__ */ jsxs13(TranslationProvider, { loadJson, children: [
    /* @__PURE__ */ jsx23(TopEventsEngine, {}),
    /* @__PURE__ */ jsx23(TopEvents, {})
  ] }) }) });
};

// src/open-bets/providers/open-bets.provider.tsx
import { sendSportsbookStatusEvent } from "@repo/core-api";
import { EnvConfig as EnvConfig2 } from "@repo/env-config";
import { useContext as useContext2, useEffect as useEffect8, useState as useState9 } from "react";

// src/open-bets/constants/open-bets-context.const.ts
import { createContext as createContext2 } from "react";
var OpenBetsContext = createContext2({
  openBetsCount: 0
});

// src/open-bets/hooks/use-kambi-wapi-for-open-bets.hook.ts
import { useEffect as useEffect6, useState as useState8 } from "react";
var useKambiWapiForOpenBets = () => {
  const [kambiApi, setKambiApi] = useState8(null);
  const [isReady, setIsReady] = useState8(false);
  useEffect6(() => {
    const checkWidget = () => {
      const kambiWindow = window;
      if (kambiWindow.KambiWidget?.ready) {
        kambiWindow.KambiWidget.ready.then((api) => {
          setKambiApi(api);
          setIsReady(true);
        }).catch((_error) => {
        });
      }
    };
    checkWidget();
    const interval = setInterval(checkWidget, 1e3);
    return () => clearInterval(interval);
  }, []);
  return { kambiApi, isReady };
};

// src/open-bets/hooks/use-open-bets-event-handlers.hook.ts
import { useCallback as useCallback14 } from "react";

// src/open-bets/constants/kambi-events.const.ts
var KAMBI_RESPONSE_TYPES = {
  BET_HISTORY: "BetHistory"
};

// src/open-bets/hooks/use-open-bets-event-handlers.hook.ts
var useKambiEventHandlers = (setKambiOpenBetsCount, sendSportsbookStatusEvent2) => {
  const handleBetHistoryUpdate = useCallback14(
    (response) => {
      if (response?.type === KAMBI_RESPONSE_TYPES.BET_HISTORY) {
        const pendingCount = response.data?.coupons?.length || 0;
        setKambiOpenBetsCount(pendingCount);
        sendSportsbookStatusEvent2(pendingCount);
      }
    },
    [setKambiOpenBetsCount, sendSportsbookStatusEvent2]
  );
  return { handleBetHistoryUpdate };
};

// src/open-bets/hooks/use-open-bets-polling.hook.ts
import { useCallback as useCallback15, useEffect as useEffect7 } from "react";

// src/open-bets/utils/open-bets-api.utils.ts
var requestPendingBets = (api) => {
  api.request(api.BET_HISTORY, {
    status: api.FETCH_COUPON_STATUS.PENDING
  });
};
var refreshPendingBets = async (isReady, kambiApi) => {
  if (isReady && kambiApi) {
    requestPendingBets(kambiApi);
  } else if (window?.KambiWidget?.ready) {
    const api = await window.KambiWidget.ready;
    requestPendingBets(api);
  }
};

// src/open-bets/hooks/use-open-bets-polling.hook.ts
var useOpenBetsPolling = (isReady, kambiApi, pollingInterval, shouldPoll) => {
  const pollingCallback = useCallback15(() => {
    if (!isReady || !kambiApi) return;
    requestPendingBets(kambiApi);
  }, [isReady, kambiApi]);
  useEffect7(() => {
    if (!shouldPoll) return;
    const interval = setInterval(pollingCallback, pollingInterval);
    return () => clearInterval(interval);
  }, [shouldPoll, pollingCallback, pollingInterval]);
  return { pollingCallback };
};

// src/open-bets/providers/open-bets.provider.tsx
import { jsx as jsx24 } from "react/jsx-runtime";
var OpenBetsProvider = ({
  children,
  isSportsEnabled
}) => {
  const { isReady, kambiApi } = useKambiWapiForOpenBets();
  const [kambiOpenBetsCount, setKambiOpenBetsCount] = useState9(0);
  const pollingInterval = EnvConfig2.OPEN_BETS_POLLING_INTERVAL;
  const { handleBetHistoryUpdate } = useKambiEventHandlers(
    setKambiOpenBetsCount,
    sendSportsbookStatusEvent
  );
  const shouldPoll = Boolean(isSportsEnabled && isReady && kambiApi);
  useOpenBetsPolling(isReady, kambiApi, pollingInterval, shouldPoll);
  useEffect8(() => {
    if (!isReady || !kambiApi || !isSportsEnabled) return;
    kambiApi.subscribe(handleBetHistoryUpdate);
    requestPendingBets(kambiApi);
  }, [isReady, kambiApi, isSportsEnabled, handleBetHistoryUpdate]);
  useKambiNotifications({
    onCashedIn: () => refreshPendingBets(isReady, kambiApi),
    onPlacedBet: () => refreshPendingBets(isReady, kambiApi)
  });
  return /* @__PURE__ */ jsx24(
    OpenBetsContext.Provider,
    {
      value: {
        openBetsCount: kambiOpenBetsCount
      },
      children
    }
  );
};
var useOpenBets = () => {
  return useContext2(OpenBetsContext);
};
export {
  Boosted,
  DISABLED_BUTTON_TEXT,
  FomoL,
  FomoM,
  FomoS,
  Football,
  Heading,
  KAMBI_EVENTS,
  OddsBoostButton,
  OddsButton,
  OpenBetsProvider,
  Pin,
  SportsCard,
  SportsCardSkeleton,
  SportsMarket,
  SportsOddsSet,
  SportsParticipants,
  SportsTimeLeague,
  ToggleButton,
  ToggleButtonContent,
  ToggleButtonIcon,
  ToggleButtonText,
  TopEventCard,
  TopEvents,
  TopEventsContainer,
  TopEventsEngine,
  getSportIcon,
  oddsBoostButtonVariants,
  oddsButtonVariants,
  renderIcon,
  sportsbookSelectors,
  sportsbookStore,
  toggleButtonClasses,
  toggleButtonLayout,
  transformToSportsCardData,
  useBetOffer,
  useBetOffersActions,
  useBetslip,
  useBetslipWithState,
  useEvent,
  useEventsActions,
  useKambiNotifications,
  useKambiWebApi,
  useOddsBoostButton,
  useOddsButton,
  useOpenBets,
  usePrePack,
  usePrePacksActions,
  useSportsbookStore,
  useToggleButton,
  useToggleButtonContext,
  useTopEvent,
  useTopEventsActions,
  useUpdateBetOffer,
  useUpdateEvent,
  useUpdatePrePack,
  useUpdateTopEvent
};
